import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useAccount } from '../contexts/AccountContext';
import invitationService from '../services/invitationService';
import './InvitationAccept.css';

const InvitationAccept = () => {
  const navigate = useNavigate();
  const { checkUser, refreshPendingInvitations } = useAuth();
  const { triggerAccountsRefresh } = useAccount();
  const [invitations, setInvitations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(null);
  const [error, setError] = useState('');

  useEffect(() => {
    loadInvitations();
  }, []);

  const loadInvitations = async () => {
    try {
      const response = await invitationService.getPendingInvitations();
      setInvitations(response.invitations || []);
    } catch (err) {
      setError('Failed to load invitations.');
    } finally {
      setLoading(false);
    }
  };

  const handleAccept = async (id) => {
    setProcessing(id);
    setError('');
    try {
      const accepted = invitations.find(inv => inv.id === id);
      await invitationService.acceptInvitation(id);
      const updated = invitations.filter(inv => inv.id !== id);
      setInvitations(updated);
      triggerAccountsRefresh();
      // Refresh user so isPartner/partnerId are immediately reflected in the navbar
      if (accepted?.type === 'partner') await checkUser();
      if (updated.length === 0) refreshPendingInvitations();
    } catch (err) {
      setError(err.response?.data?.error?.message || 'Failed to accept invitation.');
    } finally {
      setProcessing(null);
    }
  };

  const handleDecline = async (id) => {
    setProcessing(id);
    setError('');
    try {
      await invitationService.declineInvitation(id);
      const updated = invitations.filter(inv => inv.id !== id);
      setInvitations(updated);
      if (updated.length === 0) refreshPendingInvitations();
    } catch (err) {
      setError(err.response?.data?.error?.message || 'Failed to decline invitation.');
    } finally {
      setProcessing(null);
    }
  };

  if (loading) {
    return (
      <div className="invitation-container">
        <p>Loading invitations...</p>
      </div>
    );
  }

  if (invitations.length === 0) {
    return (
      <div className="invitation-container">
        <div className="invitation-done-card">
          <h2>You're all set!</h2>
          <p>No pending invitations.</p>
          <button className="btn btn-primary" onClick={() => navigate('/dashboard')}>
            Go to Dashboard
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="invitation-container">
      <div className="invitation-header">
        <h1>Pending Invitations</h1>
        <p>You have been invited to collaborate. Accept or decline each invitation below.</p>
      </div>

      {error && <div className="invitation-error">{error}</div>}

      <div className="invitation-list">
        {invitations.map(inv => (
          <div className="invitation-card" key={inv.id}>
            <div className="invitation-info">
              {/* A VENDOR INVITATION ('V') is to the vendor, not the partner: its name and the person's role there come
                  from the server (vendor_name / vendor_role, HIL-128). 'V' itself is not an access level. */}
              {(() => {
                const isVendor = inv.type === 'partner' && inv.permission_type === 'V';
                const role = isVendor ? inv.vendor_role : inv.permission_type;
                return (
                  <>
                    <span className={`invitation-type-badge invitation-type-${isVendor ? 'vendor' : inv.type}`}>
                      {isVendor ? 'Vendor' : inv.type === 'hotel' ? 'Hotel' : inv.type === 'account' ? 'Hotel Chain' : 'Partner'}
                    </span>
                    <p className="invitation-resource-name">
                      {isVendor && inv.vendor_name
                        // New invitations already read "Eleanor (hconnect)"; older ones hold only the partner's name.
                        ? (inv.resource_name && inv.resource_name.startsWith(inv.vendor_name)
                            ? inv.resource_name
                            : `${inv.vendor_name}${inv.resource_name ? ` (${inv.resource_name})` : ''}`)
                        : (inv.resource_name || `${inv.type} #${inv.resource_id}`)}
                    </p>
                    <p className="invitation-permission">
                      Access level: {role === 'A' ? 'Administrator' : 'User'}
                    </p>
                  </>
                );
              })()}
            </div>
            <div className="invitation-actions">
              <button
                className="btn-accept"
                onClick={() => handleAccept(inv.id)}
                disabled={processing === inv.id}
              >
                {processing === inv.id ? 'Processing...' : 'Accept'}
              </button>
              <button
                className="btn-decline"
                onClick={() => handleDecline(inv.id)}
                disabled={processing === inv.id}
              >
                Decline
              </button>
            </div>
          </div>
        ))}
      </div>

      {invitations.length > 0 && (
        <div style={{ marginTop: '1.5rem', textAlign: 'right' }}>
          <button className="btn btn-secondary" onClick={() => navigate('/dashboard')}>
            Handle later
          </button>
        </div>
      )}
    </div>
  );
};

export default InvitationAccept;
